import React, { useState } from "react";
import { Typography, Paper, Grid as GridBase } from "@mui/material";
import { Icon } from "@mui/material";

import RpMUIModal from "./RpMUIModal";
import StatusChipBase from "../muiCustom/chip/StatusChip";
import useSearchFilter from "@/helpers/hooks/useSearchFilter";
import NoDataFound from "../NoDataFound";
import { getStatusCode } from "@/helpers/utils";
import MuiSearchInputBase from "../muiCustom/search/MuiSearchInput";

type LocationListItem = {
  id?: string | number;
  locationName?: string;
  location_status?: string;
  [key: string]: unknown;
};

type LocationListModalProps = {
  locations?: LocationListItem[];
  showStatus?: boolean;
};

const Grid = GridBase as React.ComponentType<any>;
const StatusChip = StatusChipBase as React.ComponentType<any>;
const MuiSearchInput = MuiSearchInputBase as React.ComponentType<any>;

const LocationListModal = ({
  locations = [],
  showStatus = true,
}: LocationListModalProps) => {
  const [showLocationModal, setShowLocationModal] = useState(false);
  // Search functionality using custom hook

  const [searchTerm, handleSearchChange, filteredLocations] = useSearchFilter(
    locations,
    ["locationName"]
  );
  const onClose = () => {
    setShowLocationModal(false);
    handleSearchChange("");
  };
  return (
    <>
      <Typography
        variant="caption"
        component="span"
        className="cursor-p fw-b"
        sx={{ color: "var(--primary-base)" }}
        onClick={() => setShowLocationModal(true)}
      >
        {locations.length}
      </Typography>

      <RpMUIModal
        open={showLocationModal}
        onClose={() => {
          onClose();
          handleSearchChange("");
        }}
        maxWidth="sm"
        fullWidth
        className="p-0"
      >
        <div className="location-list-modal p-1">
          <Icon
            component="img"
            src="/img/icons/black-cross-icon.png"
            className="close-icon"
            onClick={() => {
              onClose();
              handleSearchChange("");
            }}
          />
          {/* Search Field */}
          <Grid
            container
            spacing={1}
            className="mb-1"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
              <MuiSearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                type="search"
                variant="outlined"
                placeholder="Search.."
                className="mt-3 mt-lg-1 mt-lx-0"
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            className="px-3 py-2 border-bottom"
            sx={{
              backgroundColor: "var(--neutral-100)",
              display: { xs: "none", sm: "block" },
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                size={
                  showStatus
                    ? { xs: 6, sm: 6, md: 6, lg: 6 }
                    : { xs: 12, sm: 12, md: 12, lg: 12 }
                }
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <Typography variant="caption" className="fw-sb mb-0">
                  Locations Name
                </Typography>
              </Grid>

              {showStatus ? (
                <Grid
                  item
                  size={{ xs: 6, sm: 6, md: 6, lg: 6 }}
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography
                    variant="caption"
                    className="fw-sb mb-0 mr-4 pr-2"
                  >
                    Status
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Paper>

          {/* Location List */}
          <div className="location-list-container py-1">
            {filteredLocations.length === 0 ? (
              <NoDataFound message="No locations found" />
            ) : (
              <>
                {filteredLocations.map((loc, index) => (
                  <Paper
                    key={loc.id ?? index}
                    elevation={0}
                    className="px-3 py-2"
                  >
                    <Grid container spacing={2}>
                      <Grid
                        item
                        size={
                          showStatus
                            ? { xs: 12, sm: 6, md: 6, lg: 6 }
                            : { xs: 12, sm: 12, md: 12, lg: 12 }
                        }
                        className="d-flex align-items-center justify-content-start"
                      >
                        <Typography
                          className="text-truncate fw-sb"
                          title={loc.locationName}
                          variant="body2"
                        >
                          {loc.locationName}
                        </Typography>
                      </Grid>
                      {showStatus ? (
                        <Grid
                          item
                          size={{ xs: 12, sm: 6, md: 6, lg: 6 }}
                          className="d-flex align-items-center justify-content-sm-end justify-content-xs-start"
                        >
                          <StatusChip
                            label={loc.location_status}
                            status={getStatusCode(loc.location_status, "hold")}
                            showOnlyLabel
                          />
                        </Grid>
                      ) : null}
                    </Grid>
                  </Paper>
                ))}
              </>
            )}
          </div>
        </div>
      </RpMUIModal>
    </>
  );
};

export default LocationListModal;
